import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type {
  FilterMyCompanyTasksDto,
  ChangeMyStatusDto,
  CreateMessageDto,
  UpdateMessageDto,
} from '@gokrew/models/tasks';
import {
  getMyAdminTasks,
  getMyAdminTasksSummary,
  getMyAdminTaskById,
  updateMyAdminTaskStatus,
  getOrCreateAdminTaskThread,
  getAdminTaskThread,
  getAdminTaskThreadMessages,
  postAdminTaskMessage,
  editAdminTaskMessage,
  deleteAdminTaskMessage,
  getMyAdminTaskActivity,
} from '@/api/admin-tasks';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';

const QK = {
  list: QueryKeys.AdminMyTasksList,
  detail: QueryKeys.AdminMyTasksDetail,
  thread: QueryKeys.AdminMyTasksThread,
  messages: QueryKeys.AdminMyTasksMessages,
  summary: QueryKeys.AdminMyTasksSummary,
} as const;

export function useGetMyAdminTasks(params: FilterMyCompanyTasksDto = {}) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QK.list, companyId, params],
    queryFn: () => getMyAdminTasks(companyId!, params),
    placeholderData: keepPreviousData,
    enabled: !!companyId,
    select: (res) => res.data,
  });
}

export function useGetAdminTasksSummary() {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QK.summary, companyId],
    queryFn: () => getMyAdminTasksSummary(companyId!),
    enabled: !!companyId,
    select: (res) => res.data.data,
  });
}

export function useGetMyAdminTaskById(assignmentId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QK.detail, companyId, assignmentId],
    queryFn: () => getMyAdminTaskById(companyId!, assignmentId!),
    enabled: !!companyId && !!assignmentId,
    select: (res) => res.data.data,
  });
}

export function useUpdateMyAdminTaskStatus() {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.AdminTasks);

  return useMutation({
    mutationFn: ({
      assignmentId,
      payload,
    }: {
      assignmentId: string;
      payload: ChangeMyStatusDto;
    }) => updateMyAdminTaskStatus(companyId!, assignmentId, payload),
    onSuccess: (_, { assignmentId }) => {
      toast.success(t('tasks.statusUpdated'));
      qc.invalidateQueries({ queryKey: [QK.list, companyId] });
      qc.invalidateQueries({ queryKey: [QK.detail, companyId, assignmentId] });
      qc.invalidateQueries({ queryKey: [QK.summary, companyId] });
    },
    onError: () => toast.error(t('tasks.failedUpdateStatus')),
  });
}

// ─── Thread ───────────────────────────────────────────────────

export function useGetOrCreateAdminTaskThread() {
  const { companyId } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) =>
      getOrCreateAdminTaskThread(companyId!, assignmentId),
    onSuccess: (_, assignmentId) => {
      qc.invalidateQueries({ queryKey: [QK.detail, companyId, assignmentId] });
    },
  });
}

export function useGetAdminTaskThread(threadId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QK.thread, companyId, threadId],
    queryFn: () => getAdminTaskThread(companyId!, threadId!),
    enabled: !!companyId && !!threadId,
    select: (res) => res.data,
  });
}

export function useGetAdminTaskThreadMessages(threadId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: [QK.messages, companyId, threadId],
    queryFn: () => getAdminTaskThreadMessages(companyId!, threadId!),
    enabled: !!companyId && !!threadId,
    select: (res) => res.data.data,
  });
}

export function usePostAdminTaskMessage() {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.AdminTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      payload,
    }: {
      threadId: string;
      payload: CreateMessageDto;
    }) => postAdminTaskMessage(companyId!, threadId, payload),
    onSuccess: (_, { threadId }) => {
      qc.invalidateQueries({ queryKey: [QK.messages, companyId, threadId] });
      qc.invalidateQueries({ queryKey: [QK.thread, companyId, threadId] });
      qc.invalidateQueries({ queryKey: [QK.detail, companyId] });
    },
    onError: () => toast.error(t('tasks.failedSendMessage')),
  });
}

export function useEditAdminTaskMessage() {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.AdminTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
      payload,
    }: {
      threadId: string;
      messageId: string;
      payload: UpdateMessageDto;
    }) => editAdminTaskMessage(companyId!, threadId, messageId, payload),
    onSuccess: (_, { threadId }) => {
      qc.invalidateQueries({ queryKey: [QK.messages, companyId, threadId] });
      qc.invalidateQueries({ queryKey: [QK.detail, companyId] });
    },
    onError: () => toast.error(t('tasks.failedEditMessage')),
  });
}

export function useDeleteAdminTaskMessage() {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.AdminTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
    }: {
      threadId: string;
      messageId: string;
    }) => deleteAdminTaskMessage(companyId!, threadId, messageId),
    onSuccess: (_, { threadId }) => {
      toast.success(t('tasks.messageDeleted'));
      qc.invalidateQueries({ queryKey: [QK.messages, companyId, threadId] });
      qc.invalidateQueries({ queryKey: [QK.detail, companyId] });
    },
    onError: () => toast.error(t('tasks.failedDeleteMessage')),
  });
}

export function useGetAdminTaskActivity(assignmentId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QK.detail, companyId, assignmentId, 'activity'],
    queryFn: () => getMyAdminTaskActivity(companyId!, assignmentId!),
    enabled: !!companyId && !!assignmentId,
    select: (res) => res.data.data,
  });
}
